// Exportació a .xlsx (via exceljs) d'una taula de "Detall" (desglose) agrupada per capítol —
// extret de NodeForm.tsx (Escandalls) el 2026-10-08 perque ExpedientDetail.tsx (Expedients de
// Taller) reutilitzés EXACTAMENT la mateixa utilitat en comptes de duplicar-la una altra vegada.
// BudgetForm.tsx te la seva pròpia `exportBudgetDetallToExcel` (treballa directament sobre
// BudgetLine[], no sobre aquest DetallDisplayRow aplanat) — no es toca aquí.

export const fmt2 = (v: number | undefined) =>
  Number(v || 0).toLocaleString('ca-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
export const fmt3 = (v: number | undefined) =>
  Number(v || 0).toLocaleString('ca-ES', { minimumFractionDigits: 3, maximumFractionDigits: 3 });

export interface DetallDisplayRow {
  key: string;
  num: string;
  cap: string;
  planol: string;
  comentari: string;
  element: string;
  badge: { text: string; cls: string };
  ua: string;
  un: string;
  a: string;
  b: string;
  c: string;
  amidament: string;
  locked: boolean;
  costU: number;
  costTotal: number;
  marge: number | null;
  descompte: string;
  preu: number;
  import: number;
  observacions: string;
  pes: number;
  pctImport?: number;
  elementImageUrl?: string | null;
}

// Índex (0-based) de la columna "Foto" a detallHeaders/detallRowToCells — usat per
// exportDetallToExcel (i pel print a NodeForm.tsx) per substituir aquesta cel·la per una
// imatge en lloc de text.
export const DETALL_FOTO_COL_INDEX = 6;

export function detallHeaders(withPctImport?: boolean): string[] {
  const base = ['#', 'Cap.', 'Ref. Plànol', 'Descripció', 'Operació', 'Element', 'Foto', 'Tipus', 'UA', 'Q.', 'Mida A', 'Mida B', 'Mida C', 'Amidament', 'Bloq.', 'Cost/U', 'Cost total', 'Marge', 'Descompte', 'Preu', 'Import'];
  if (withPctImport) base.push('% Import');
  base.push('Pes');
  return base;
}

export function detallRowToCells(r: DetallDisplayRow, withPctImport?: boolean): string[] {
  const cells = [
    r.num, r.cap, r.planol, r.comentari, r.observacions, r.element, '', r.badge.text, r.ua, r.un, r.a, r.b, r.c,
    r.amidament, r.locked ? 'Sí' : '', fmt2(r.costU), fmt2(r.costTotal),
    r.marge != null ? fmt2(r.marge) : '', r.descompte, fmt2(r.preu), fmt2(r.import),
  ];
  if (withPctImport) cells.push(r.pctImport != null ? fmt2(r.pctImport) : '');
  cells.push(fmt2(r.pes));
  return cells;
}

// Nombre de decimals que Excel ha de forçar per columna (null = no és una columna numèrica) —
// mateix ordre que detallHeaders/detallRowToCells. Sense això, Excel interpreta els valors com a
// números i els mostra amb format "General" (retallant els zeros finals).
export function detallNumFormats(withPctImport?: boolean): (number | null)[] {
  const base: (number | null)[] = [null, null, null, null, null, null, null, null, null, 2, 3, 3, 3, 2, null, 2, 2, 2, 2, 2, 2];
  if (withPctImport) base.push(2);
  base.push(2);
  return base;
}

export function sanitizeFilename(v: string): string {
  return v.replace(/[/\\:*?"<>|]/g, '-').trim();
}

// Descarrega una imatge i la retorna com a data URI (base64) + extensió, per poder-la incrustar
// com a imatge nativa dins l'.xlsx (exceljs).
export async function fetchImageForExcel(url: string): Promise<{ dataUri: string; extension: 'jpeg' | 'png' | 'gif' } | null> {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    const mime = blob.type || '';
    const extension: 'jpeg' | 'png' | 'gif' = mime.includes('png') ? 'png' : mime.includes('gif') ? 'gif' : 'jpeg';
    const dataUri = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
    return { dataUri, extension };
  } catch {
    return null;
  }
}

// Converteix un valor ja formatat amb fmt2/fmt3 (locale ca-ES: punt de milers, coma decimal) de
// nou a número — cal per guardar-lo com a cel·la numèrica real a l'.xlsx (no com a text).
export function parseLocaleNumber(s: string): number | null {
  if (!s) return null;
  const n = Number(s.replace(/\./g, '').replace(',', '.'));
  return Number.isNaN(n) ? null : n;
}

export function excelNumFmt(decimals: number): string {
  return decimals > 0 ? `#,##0.${'0'.repeat(decimals)}` : '#,##0';
}

const DETALL_COL_WIDTHS = [4, 8, 12, 24, 24, 28, 6, 7, 6, 6, 7, 7, 7, 10, 5, 9, 10, 7, 9, 8, 9];
export function detallColWidths(withPctImport?: boolean): number[] {
  const base = [...DETALL_COL_WIDTHS];
  if (withPctImport) base.push(9);
  base.push(7);
  return base;
}

// Genera un .xlsx real (via exceljs) amb la miniatura de l'element incrustada com a imatge
// nativa a la cel·la "Foto" — un .xls fet amb una taula HTML no permet incrustar imatges de
// manera fiable a Excel.
export async function exportDetallToExcel(
  filename: string,
  groups: { key: string; label: string; rows: DetallDisplayRow[] }[],
  withPctImport?: boolean,
  title?: string,
) {
  const headers = detallHeaders(withPctImport);
  const rowFormats = detallNumFormats(withPctImport);
  const widths = detallColWidths(withPctImport);
  const costTotalCol = headers.indexOf('Cost total');
  const margeCol = headers.indexOf('Marge');
  const importCol = headers.indexOf('Import');
  const pctImportCol = withPctImport ? headers.indexOf('% Import') : -1;
  const pesCol = headers.length - 1;

  const imageUrls = Array.from(new Set(
    groups.flatMap((g) => g.rows.map((r) => r.elementImageUrl).filter((u): u is string => !!u))
  ));
  const imageMap = new Map<string, { dataUri: string; extension: 'jpeg' | 'png' | 'gif' }>();
  await Promise.all(imageUrls.map(async (u) => {
    const img = await fetchImageForExcel(u);
    if (img) imageMap.set(u, img);
  }));

  // Càrrega diferida — exceljs pesa ~1MB minificat, no cal incloure'l al bundle principal si
  // l'usuari no exporta mai a Excel.
  const { default: ExcelJS } = await import('exceljs');
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet('Detall');
  sheet.columns = widths.map((w) => ({ width: w }));

  if (title) {
    const titleRow = sheet.addRow([title]);
    titleRow.font = { bold: true, size: 14 };
    sheet.mergeCells(titleRow.number, 1, titleRow.number, headers.length);
    sheet.addRow([]);
  }

  const headerRow = sheet.addRow(headers);
  headerRow.font = { bold: true };
  headerRow.eachCell((cell) => { cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE2E8F0' } }; });

  const addSummaryRow = (label: string, rows: DetallDisplayRow[]) => {
    const informedMarge = rows.map((r) => r.marge).filter((m): m is number => m != null && m > 0);
    const costTotal = rows.reduce((s, r) => s + r.costTotal, 0);
    const marge = informedMarge.length > 0 ? informedMarge.reduce((s, m) => s + m, 0) / informedMarge.length : null;
    const importTotal = rows.reduce((s, r) => s + r.import, 0);
    const pctImportTotal = rows.reduce((s, r) => s + (r.pctImport ?? 0), 0);
    const pes = rows.reduce((s, r) => s + r.pes, 0);

    const values: (string | number)[] = new Array(headers.length).fill('');
    values[costTotalCol] = costTotal;
    if (marge != null) values[margeCol] = marge;
    values[importCol] = importTotal;
    if (withPctImport) values[pctImportCol] = pctImportTotal;
    values[pesCol] = pes;

    const row = sheet.addRow(values);
    row.font = { bold: true };
    row.eachCell((cell) => { cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8FAFC' } }; });
    sheet.mergeCells(row.number, 1, row.number, costTotalCol);
    row.getCell(1).value = label;
    row.getCell(1).alignment = { horizontal: 'right' };
    row.getCell(costTotalCol + 1).numFmt = excelNumFmt(2);
    if (marge != null) row.getCell(margeCol + 1).numFmt = excelNumFmt(2);
    row.getCell(importCol + 1).numFmt = excelNumFmt(2);
    if (withPctImport) row.getCell(pctImportCol + 1).numFmt = '0.00" %"';
    row.getCell(pesCol + 1).numFmt = excelNumFmt(2);
  };

  groups.forEach((g) => {
    const groupRow = sheet.addRow([g.label]);
    groupRow.font = { bold: true };
    groupRow.eachCell((cell) => { cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF1F5F9' } }; });
    sheet.mergeCells(groupRow.number, 1, groupRow.number, headers.length);

    g.rows.forEach((r) => {
      const cells = detallRowToCells(r, withPctImport);
      const values = cells.map((c, i) => {
        if (i === DETALL_FOTO_COL_INDEX) return '';
        const fmt = rowFormats[i];
        return fmt == null ? c : (parseLocaleNumber(c) ?? '');
      });
      const row = sheet.addRow(values);
      cells.forEach((_, i) => {
        const fmt = rowFormats[i];
        if (fmt != null && i !== DETALL_FOTO_COL_INDEX) row.getCell(i + 1).numFmt = excelNumFmt(fmt);
      });
      const img = r.elementImageUrl ? imageMap.get(r.elementImageUrl) : null;
      if (img) {
        const imgId = workbook.addImage({ base64: img.dataUri, extension: img.extension });
        sheet.addImage(imgId, {
          tl: { col: DETALL_FOTO_COL_INDEX, row: row.number - 1 },
          ext: { width: 32, height: 32 },
          editAs: 'oneCell',
        } as any);
        row.height = 26;
      }
    });

    addSummaryRow(`Subtotal ${g.label}`, g.rows);
  });

  if (groups.length > 1) {
    addSummaryRow('Totals', groups.flatMap((g) => g.rows));
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  // Noms de fitxer massa llargs (p.ex. construïts a partir d'un concepte de desenes de
  // paraules) fan fallar la descarrega/obertura a Windows (límit de longitud de ruta) —
  // es trunca aquí com a darrera defensa, encara que el crider ja hauria d'evitar-ho.
  const safeName = filename.replace(/\.xls$/i, '.xlsx');
  a.download = safeName.length > 150 ? `${safeName.slice(0, 146)}….xlsx` : safeName;
  a.click();
  URL.revokeObjectURL(url);
}

// Resum d'una partida (línia de pressupost) — a diferencia d'un escandall (NodeForm.tsx), a
// l'Expedient de Taller cada línia te el seu propi Qt/Preu/Resultat/Marge% ABANS d'entrar al
// seu desglose (vegeu ExpedientDetail.tsx "Línies del Expedient").
export interface PartidaSummary {
  ordre: string | number;
  concepte: string;
  qt: number;
  ut: string;
  totalCost: number;
  preu: number;
  preuU: number;
  resultat: number;
  margePct: number;
}

// Com exportDetallToExcel pero amb un nivell extra: cada "línia" (partida) porta el seu propi
// resum (Qt/Ut/Concepte/Total cost/Preu/Preu-U/Resultat/Marge%) com a fila pròpia, seguit del
// seu desglose agrupat per capítol — mateixa idea que addSummaryRow (reutilitza columnes ja
// existents del Detall: Cost/U->Preu/U, Cost total, Marge->Marge%, Preu, Import->Resultat).
export async function exportLiniesAmbPartidesToExcel(
  filename: string,
  lines: { partida: PartidaSummary; groups: { key: string; label: string; rows: DetallDisplayRow[] }[] }[],
  title?: string,
) {
  const headers = detallHeaders();
  const rowFormats = detallNumFormats();
  const widths = detallColWidths();
  const numCol = headers.indexOf('#');
  const descCol = headers.indexOf('Descripció');
  const qCol = headers.indexOf('Q.');
  const uaCol = headers.indexOf('UA');
  const costUCol = headers.indexOf('Cost/U');
  const costTotalCol = headers.indexOf('Cost total');
  const margeCol = headers.indexOf('Marge');
  const preuCol = headers.indexOf('Preu');
  const importCol = headers.indexOf('Import');
  const pesCol = headers.length - 1;

  const allRows = lines.flatMap((l) => l.groups.flatMap((g) => g.rows));
  const imageUrls = Array.from(new Set(allRows.map((r) => r.elementImageUrl).filter((u): u is string => !!u)));
  const imageMap = new Map<string, { dataUri: string; extension: 'jpeg' | 'png' | 'gif' }>();
  await Promise.all(imageUrls.map(async (u) => {
    const img = await fetchImageForExcel(u);
    if (img) imageMap.set(u, img);
  }));

  const { default: ExcelJS } = await import('exceljs');
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet('Detall');
  sheet.columns = widths.map((w) => ({ width: w }));

  if (title) {
    const titleRow = sheet.addRow([title]);
    titleRow.font = { bold: true, size: 14 };
    sheet.mergeCells(titleRow.number, 1, titleRow.number, headers.length);
    sheet.addRow([]);
  }

  const headerRow = sheet.addRow(headers);
  headerRow.font = { bold: true };
  headerRow.eachCell((cell) => { cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE2E8F0' } }; });

  const addSummaryRow = (label: string, rows: DetallDisplayRow[]) => {
    const informedMarge = rows.map((r) => r.marge).filter((m): m is number => m != null && m > 0);
    const costTotal = rows.reduce((s, r) => s + r.costTotal, 0);
    const marge = informedMarge.length > 0 ? informedMarge.reduce((s, m) => s + m, 0) / informedMarge.length : null;
    const importTotal = rows.reduce((s, r) => s + r.import, 0);
    const pes = rows.reduce((s, r) => s + r.pes, 0);

    const values: (string | number)[] = new Array(headers.length).fill('');
    values[costTotalCol] = costTotal;
    if (marge != null) values[margeCol] = marge;
    values[importCol] = importTotal;
    values[pesCol] = pes;

    const row = sheet.addRow(values);
    row.font = { bold: true };
    row.eachCell((cell) => { cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8FAFC' } }; });
    sheet.mergeCells(row.number, 1, row.number, costTotalCol);
    row.getCell(1).value = label;
    row.getCell(1).alignment = { horizontal: 'right' };
    row.getCell(costTotalCol + 1).numFmt = excelNumFmt(2);
    if (marge != null) row.getCell(margeCol + 1).numFmt = excelNumFmt(2);
    row.getCell(importCol + 1).numFmt = excelNumFmt(2);
    row.getCell(pesCol + 1).numFmt = excelNumFmt(2);
  };

  lines.forEach(({ partida, groups }) => {
    // Fila resum de la partida — mateix color que fan servir altres pantalles per "dades
    // principals" (blau clar), ben diferenciada de les files de capítol (gris) i de detall.
    const values: (string | number)[] = new Array(headers.length).fill('');
    values[numCol] = partida.ordre;
    values[descCol] = partida.concepte;
    values[qCol] = partida.qt;
    values[uaCol] = partida.ut;
    values[costUCol] = partida.preuU;
    values[costTotalCol] = partida.totalCost;
    values[margeCol] = partida.margePct;
    values[preuCol] = partida.preu;
    values[importCol] = partida.resultat;

    const partidaRow = sheet.addRow(values);
    partidaRow.font = { bold: true };
    partidaRow.eachCell((cell) => { cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDCE7FB' } }; });
    partidaRow.getCell(qCol + 1).numFmt = excelNumFmt(2);
    partidaRow.getCell(costUCol + 1).numFmt = excelNumFmt(2);
    partidaRow.getCell(costTotalCol + 1).numFmt = excelNumFmt(2);
    partidaRow.getCell(margeCol + 1).numFmt = '0.00" %"';
    partidaRow.getCell(preuCol + 1).numFmt = excelNumFmt(2);
    partidaRow.getCell(importCol + 1).numFmt = excelNumFmt(2);

    groups.forEach((g) => {
      const groupRow = sheet.addRow([`    ${g.label}`]);
      groupRow.font = { italic: true };
      groupRow.eachCell((cell) => { cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF1F5F9' } }; });
      sheet.mergeCells(groupRow.number, 1, groupRow.number, headers.length);

      g.rows.forEach((r) => {
        const cells = detallRowToCells(r);
        const rowValues = cells.map((c, i) => {
          if (i === DETALL_FOTO_COL_INDEX) return '';
          const fmt = rowFormats[i];
          return fmt == null ? c : (parseLocaleNumber(c) ?? '');
        });
        const row = sheet.addRow(rowValues);
        cells.forEach((_, i) => {
          const fmt = rowFormats[i];
          if (fmt != null && i !== DETALL_FOTO_COL_INDEX) row.getCell(i + 1).numFmt = excelNumFmt(fmt);
        });
        const img = r.elementImageUrl ? imageMap.get(r.elementImageUrl) : null;
        if (img) {
          const imgId = workbook.addImage({ base64: img.dataUri, extension: img.extension });
          sheet.addImage(imgId, {
            tl: { col: DETALL_FOTO_COL_INDEX, row: row.number - 1 },
            ext: { width: 32, height: 32 },
            editAs: 'oneCell',
          } as any);
          row.height = 26;
        }
      });

      addSummaryRow(`Subtotal ${g.label}`, g.rows);
    });
  });

  if (lines.length > 1 || lines.some((l) => l.groups.length > 1)) {
    addSummaryRow('Totals desglose', lines.flatMap((l) => l.groups.flatMap((g) => g.rows)));
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const safeName = filename.replace(/\.xls$/i, '.xlsx');
  a.download = safeName.length > 150 ? `${safeName.slice(0, 146)}….xlsx` : safeName;
  a.click();
  URL.revokeObjectURL(url);
}
